"use client";

import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";

import { type Locale, NextIntlClientProvider } from "next-intl";
import {
  localeCookieNames,
  normalizeLocale,
  resolveLocale,
  serializeLocaleCookie,
  sharedLocaleCookieName,
} from "../utils/locale";
import { useTranslationMessages } from "../hooks/use-translation-messages";

type Messages = Record<string, any>;

interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (locale: string) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

interface LanguageProviderProps {
  children: ReactNode;
  initialLocale?: Locale;
  initialMessages?: Messages;
}

export const LanguageProvider = ({
  children,
  initialLocale = "en" as Locale,
  initialMessages = {},
}: LanguageProviderProps): any => {
  const [currentLanguage, setCurrentLanguage] = useState(
    resolveLocale({
      cookieValues:
        typeof document === "undefined"
          ? []
          : localeCookieNames.map((name) => {
              let value: string | null = null;
              for (const cookie of document.cookie.split(";")) {
                const [cookieName, ...cookieValueParts] = cookie
                  .trim()
                  .split("=");
                if (cookieName === name) value = cookieValueParts.join("=");
              }
              return value;
            }),
      browserLocale:
        typeof navigator === "undefined" ? null : navigator.language,
      fallback: initialLocale,
    })
  );

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    if (
      document.cookie
        .split("; ")
        .some((row) => row.startsWith(`${sharedLocaleCookieName}=`))
    )
      return;

    document.cookie = serializeLocaleCookie(
      sharedLocaleCookieName,
      currentLanguage as Locale
    );
  }, [currentLanguage]);

  const { data: messages } = useTranslationMessages({
    locale: currentLanguage,
    // RootLayout owns the initial locale's background fetch in the same cache.
    // Fetch here only when the language changes.
    enabled: currentLanguage !== initialLocale,
  });

  const setLanguage = useCallback(async (newLocale: string) => {
    const locale = normalizeLocale(newLocale);
    if (!locale) return;

    localeCookieNames.forEach((name) => {
      document.cookie = serializeLocaleCookie(name, locale);
    });
    setCurrentLanguage(locale);
  }, []);

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        setLanguage,
      }}
    >
      <NextIntlClientProvider
        messages={messages ?? initialMessages}
        locale={currentLanguage as Locale}
        timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}
      >
        {children}
      </NextIntlClientProvider>
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
